/**
 * 사용자 상세 · 기본 정보 탭의 「로그인 수단」 카드.
 *
 * 피그마 (Web·Rudalink Admin v1.0) node 13378-3716 「카드 · 로그인 수단」 기준.
 * 연결·해제와 비밀번호 설정은 사용자가 앱(07-A · 09)에서만 한다. 어드민은 조회만이라
 * 이 카드에는 조작 버튼이 없다.
 */
import {
  LOGIN_METHOD_TYPE,
  type LoginMethodType,
  type PlatformUserLoginMethod,
  type PlatformUserLoginMethodsResponse,
} from '@/entities/user/api/user.api';
import { Badge } from '@/shared/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  DISPLAY_EMPTY,
  formatDate,
  formatDateTimeMinute,
} from '@/shared/lib/dateFormat';

const METHOD_LABEL: Record<LoginMethodType, string> = {
  KAKAO: '카카오',
  NAVER: '네이버',
  GOOGLE: 'Google',
  APPLE: 'Apple',
  EMAIL: '이메일 · 비밀번호',
};

/**
 * 제공자 브랜드 색. 디자인 토큰이 아니라 각 사의 고정 브랜드 값이라 그대로 둔다.
 * 피그마의 24px 라운드 사각 아이콘과 가입 수단 칩이 같은 색을 쓴다.
 */
const METHOD_BRAND: Record<LoginMethodType, { chip: string; initial: string }> =
  {
    KAKAO: { chip: 'bg-[#fee500] text-[#3c1e1e]', initial: 'K' },
    NAVER: { chip: 'bg-[#03c75a] text-white', initial: 'N' },
    GOOGLE: {
      chip: 'border border-ruda-grey-300 bg-white text-foreground',
      initial: 'G',
    },
    APPLE: { chip: 'bg-[#111111] text-white', initial: 'A' },
    EMAIL: { chip: 'bg-ruda-grey-200 text-ruda-grey-600', initial: '@' },
  };

const methodLabel = (method: LoginMethodType): string =>
  METHOD_LABEL[method] ?? method;

/**
 * 프로젝트 Table 의 기본 스타일을 이 카드에서만 피그마에 맞춘다.
 * - 셀 세로 구분선 제거 (`[&:not(:last-child)]:border-r` 가 변형 선택자라 `border-r-0` 로는 안 꺼진다)
 * - 헤더 글자를 보라 대문자 트래킹 대신 13px 회색으로
 */
const TABLE_WRAPPER = [
  'overflow-hidden rounded-[10px] border border-ruda-grey-200',
  '[&_td]:border-r-0 [&_th]:border-r-0',
  '[&_th]:text-[13px] [&_th]:font-medium [&_th]:normal-case [&_th]:tracking-normal [&_th]:text-muted-foreground',
  '[&_td]:text-[13px]',
].join(' ');

/** 이메일 행의 「계정 정보」 안내 — 피그마 문구 그대로 */
const EMAIL_UNSET_HINT =
  '미설정 — 사용자가 앱(07-A → 09)에서 직접 만들 수 있고, 아이디는 계정 이메일로 고정됩니다';

/** 피그마 24px 라운드 사각 아이콘. 제공자 로고 에셋이 없어 머리글자로 대신한다 */
function MethodIcon({ method }: { method: LoginMethodType }) {
  const brand = METHOD_BRAND[method];
  return (
    // 이 프로젝트의 rounded-md 는 16px 라 24px 아이콘이 원형이 된다. 피그마는 6px
    <span
      aria-hidden
      className={`flex size-6 shrink-0 items-center justify-center rounded-[6px] text-[11px] font-bold ${brand.chip}`}
    >
      {brand.initial}
    </span>
  );
}

function SummaryBox({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex-1 rounded-[10px] bg-ruda-grey-50 px-4 py-3.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

/** 요약 박스의 굵은 본문 값 */
function SummaryValue({ children }: { children: React.ReactNode }) {
  return <span className="text-sm font-bold text-foreground">{children}</span>;
}

/** 요약 박스의 회색 보조 설명 */
function SummaryNote({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-muted-foreground">{children}</span>;
}

/** 계정 정보 칸의 본문 — 칩·배지 앞에 오는 이메일 또는 상태 문구 (기획 A-07) */
function AccountText({
  row,
  withdrawn,
}: {
  row: PlatformUserLoginMethod;
  withdrawn: boolean;
}) {
  if (row.method === LOGIN_METHOD_TYPE.EMAIL) {
    if (row.linked) return <span>{row.accountEmail || DISPLAY_EMPTY}</span>;
    // 탈퇴 계정은 앱에서 만들 수 없으니 안내 없이 「미설정」만
    return (
      <span className="whitespace-normal text-muted-foreground">
        {withdrawn ? '미설정' : EMAIL_UNSET_HINT}
      </span>
    );
  }
  if (!row.linked) return <span className="text-muted-foreground">미연결</span>;
  if (!row.accountEmail) {
    return <span className="text-muted-foreground">제공된 이메일 없음</span>;
  }
  return <span>{row.accountEmail}</span>;
}

/**
 * 계정 정보 칸. 칩·배지는 [가입 수단] → [비공개 이메일] → [연결 해제 …] 순서로
 * 해당하는 것만 붙인다. 가입한 SNS 를 나중에 해제해도 「미연결」 뒤에 [가입 수단]은 남는다.
 */
/**
 * 비밀번호 설정 요약의 보조 문구 (기획 A-07).
 * 탈퇴 계정은 있음·없음과 관계없이 로그인할 수 없다는 사실만 알린다.
 * 설정 일시는 이메일 가입이면 가입 일시, SNS 가입이면 앱 09 에서 만든 일시다.
 */
const passwordNote = (data: PlatformUserLoginMethodsResponse): string => {
  if (data.withdrawn) return '탈퇴 계정 · 로그인 불가';
  if (!data.password.set) return 'SNS 로만 로그인 · 비밀번호 재설정 대상 아님';
  return data.password.setAt
    ? `${formatDate(data.password.setAt)} 설정 · 비밀번호 재설정 가능`
    : '비밀번호 재설정 가능';
};

/**
 * 최근 접속 요약의 보조 문구 (기획 A-07).
 * 수단이 null 인 경우 — 수단 기록 전(V149 이전) 접속 로그이거나, 접속 로그 없이
 * 어드민이 탈퇴시킨 계정(접속 일시 = 탈퇴 일시)이다. 둘 다 「수단 기록 없음」으로 알린다.
 */
const lastAccessNote = (data: PlatformUserLoginMethodsResponse): string => {
  const method = data.lastAccess?.method;
  const parts = [
    method ? `${methodLabel(method)} 로그인 세션` : '수단 기록 없음',
    data.withdrawn && '탈퇴 전 마지막 접속',
  ].filter(Boolean);
  return parts.join(' · ');
};

function AccountCell({
  row,
  withdrawn,
}: {
  row: PlatformUserLoginMethod;
  withdrawn: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <AccountText row={row} withdrawn={withdrawn} />
      {row.signupMethod && (
        <Badge variant="secondary" className="shrink-0">
          가입 수단
        </Badge>
      )}
      {row.privateEmail && (
        <Badge variant="warning" className="shrink-0">
          비공개 이메일
        </Badge>
      )}
      {/*
       * 탈퇴 시점에 연결돼 있던 SNS 행에만 내려온다. 서버는 REVOKED 만 완료로 보고
       * 실패·건너뜀·진행 중은 모두 미완료다 (미완료여도 탈퇴 자체는 정상 처리됨).
       * 제공자 revoke 는 탈퇴 커밋 뒤 비동기라 탈퇴 직후 조회하면 진행 중일 수 있다
       */}
      {row.withdrawalUnlink && (
        <Badge
          variant={row.withdrawalUnlink.completed ? 'success' : 'destructive'}
          className="shrink-0"
          title={
            row.withdrawalUnlink.completed
              ? undefined
              : '연결 해제에 실패했거나 건너뛰었습니다. 탈퇴 직후라면 아직 진행 중일 수 있습니다.'
          }
        >
          {row.withdrawalUnlink.completed
            ? row.withdrawalUnlink.completedAt
              ? `연결 해제 완료 · ${formatDateTimeMinute(row.withdrawalUnlink.completedAt)}`
              : '연결 해제 완료'
            : '연결 해제 미완료'}
        </Badge>
      )}
    </div>
  );
}

/** 값이 없으면 피그마처럼 회색 `-` 로 비운다 */
function TimeCell({ value }: { value: string | null }) {
  if (!value) {
    return <span className="text-muted-foreground">{DISPLAY_EMPTY}</span>;
  }
  return <span>{formatDateTimeMinute(value)}</span>;
}

export interface UserLoginMethodsCardProps {
  data?: PlatformUserLoginMethodsResponse;
  isLoading: boolean;
  isError: boolean;
}

export function UserLoginMethodsCard({
  data,
  isLoading,
  isError,
}: UserLoginMethodsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          로그인 수단
          {/* 이용 여부가 「탈퇴」일 때만 짙은 회색 태그를 붙인다 (기획 A-06) */}
          {data?.withdrawn && (
            <Badge
              variant="secondary"
              className="border-transparent bg-ruda-grey-700 text-white"
            >
              탈퇴 계정
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading && (
          <p className="text-sm text-muted-foreground">
            로그인 수단을 불러오는 중...
          </p>
        )}

        {isError && (
          <p className="text-sm text-ruda-error">
            로그인 수단을 불러오지 못했습니다.
          </p>
        )}

        {data && (
          <>
            <div className="flex flex-col gap-3 sm:flex-row">
              <SummaryBox label="가입 수단">
                {/* 서버가 모르는 가입 제공자면 method 가 null 이라 칩 없이 비운다 */}
                {data.signup.method ? (
                  <Badge
                    variant="outline"
                    className={`border-transparent ${METHOD_BRAND[data.signup.method].chip}`}
                  >
                    {methodLabel(data.signup.method)}
                  </Badge>
                ) : (
                  <SummaryNote>{DISPLAY_EMPTY}</SummaryNote>
                )}
                {data.signup.signedUpAt && (
                  <span className="text-[13px] text-ruda-grey-600">
                    {formatDate(data.signup.signedUpAt)} 가입
                  </span>
                )}
              </SummaryBox>

              <SummaryBox label="비밀번호 설정">
                <SummaryValue>
                  {data.password.set ? '있음' : '없음'}
                </SummaryValue>
                <SummaryNote>{passwordNote(data)}</SummaryNote>
              </SummaryBox>

              <SummaryBox label="최근 접속">
                {data.lastAccess ? (
                  <>
                    <SummaryValue>
                      {formatDateTimeMinute(data.lastAccess.accessedAt)}
                    </SummaryValue>
                    {lastAccessNote(data) && (
                      <SummaryNote>{lastAccessNote(data)}</SummaryNote>
                    )}
                  </>
                ) : (
                  <SummaryNote>{DISPLAY_EMPTY}</SummaryNote>
                )}
              </SummaryBox>
            </div>

            {/*
             * 피그마의 표는 목록 화면의 데이터 그리드와 달리 세로 구분선이 없고
             * 헤더가 회색이다. 프로젝트 Table 의 기본 칩(세로선·보라 헤더)만 끈다.
             */}
            <div className={TABLE_WRAPPER}>
              <Table>
                <TableHeader className="[&_tr]:border-ruda-grey-200 [&_tr]:bg-ruda-grey-50">
                  <TableRow>
                    <TableHead className="w-[210px]">수단</TableHead>
                    <TableHead>계정 정보</TableHead>
                    <TableHead className="w-[190px]">연결 일시</TableHead>
                    <TableHead className="w-[190px]">최근 사용</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.methods.map((row) => (
                    <TableRow key={row.method}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <MethodIcon method={row.method} />
                          <span>{methodLabel(row.method)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <AccountCell row={row} withdrawn={data.withdrawn} />
                      </TableCell>
                      <TableCell>
                        <TimeCell value={row.linkedAt} />
                      </TableCell>
                      <TableCell>
                        <TimeCell value={row.lastUsedAt} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
